import { getPointsOrderListV2, batchLockingOrderList } from "@/api/lst";
import { payOrderForPlatform } from "@/utils/orderPayJq18";
import { getAutoOrderPayConfig } from "@/utils/autoOrderPayConfig";
import { getLstToken, getLstPhone } from "@/utils/lstSessionStorage";
import { getStoredPlatformId, PLATFORM_JQ18 } from "@/utils/apiPlatform";
import { isAuthTokenExpired } from "@/utils/authToken";
import {
    ORDER_STATUS_PENDING_PAY,
    getOrderAmountYuan,
    getOrderDisplayName,
    getOrderPayDeadlineMs
} from "@/utils/orderPayDeadline";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const recentPaid = new Map();
const RECENT_PAID_TTL_MS = 90_000;

function pruneRecentPaid() {
    const now = Date.now();
    for (const [id, ts] of recentPaid.entries()) {
        if (now - ts > RECENT_PAID_TTL_MS)
            recentPaid.delete(id);
    }
}

function mergePendingOrders(apiList, batchData) {
    const chunk = Array.isArray(batchData?.orderDetailRes) ? batchData.orderDetailRes : [];
    const base = Array.isArray(apiList) ? apiList : [];
    const seen = new Set();
    const out = [];
    for (const x of [...chunk, ...base]) {
        const id = String(x?.orderId ?? "").trim();
        if (!id || seen.has(id))
            continue;
        if (Number(x.status) !== ORDER_STATUS_PENDING_PAY)
            continue;
        seen.add(id);
        out.push(x);
    }
    return out;
}

export async function fetchPendingPayOrders(token, phone) {
    const [listRes, batchRes] = await Promise.all([
        getPointsOrderListV2(token, phone, {
            type: [0],
            page: 1,
            pageSize: 50,
            name: null,
            albumType: null,
            dateRange: null
        }),
        batchLockingOrderList(token, phone).catch(() => ({ code: 0 }))
    ]);
    const apiList = Number(listRes?.code) === 1 ? (listRes.data?.list || []) : [];
    const batchData = Number(batchRes?.code) === 1 ? batchRes.data : null;
    return mergePendingOrders(apiList, batchData);
}

export function pickOrdersToAutoPay(orders, maxAmountYuan, now = Date.now()) {
    const max = Number(maxAmountYuan);
    return [...orders]
        .filter((o) => {
            const amt = getOrderAmountYuan(o);
            return Number.isFinite(amt) && amt > 0 && amt <= max;
        })
        .sort((a, b) => getOrderPayDeadlineMs(a, now) - getOrderPayDeadlineMs(b, now));
}

/**
 * 扫描待支付订单并按剩余时间从短到长顺序静默支付。
 * @returns {{ ok: boolean, scanned: number, paid: number, message: string }}
 */
export async function runAutoOrderPayScan() {
    const config = getAutoOrderPayConfig();
    if (!config.enabled)
        return { ok: true, scanned: 0, paid: 0, message: "未开启" };

    if (getStoredPlatformId() !== PLATFORM_JQ18) {
        return { ok: false, scanned: 0, paid: 0, message: "仅支持镜权平台" };
    }

    const token = getLstToken();
    const phone = getLstPhone();
    if (!token || !phone)
        return { ok: false, scanned: 0, paid: 0, message: "请先登录" };

    const pwd = String(config.payPassword ?? "").trim();
    if (!/^\d{6}$/.test(pwd))
        return { ok: false, scanned: 0, paid: 0, message: "请配置 6 位支付密码" };

    pruneRecentPaid();

    let orders;
    try {
        orders = await fetchPendingPayOrders(token, phone);
    }
    catch (e) {
        return { ok: false, scanned: 0, paid: 0, message: e?.message || "拉取订单失败" };
    }

    const candidates = pickOrdersToAutoPay(orders, config.maxAmount);
    if (candidates.length === 0) {
        return {
            ok: true,
            scanned: orders.length,
            paid: 0,
            message: orders.length ? "无符合金额上限的待支付订单" : "暂无待支付订单"
        };
    }

    let paid = 0;
    let lastMessage = "";

    for (const order of candidates) {
        const oid = String(order.orderId);
        const last = recentPaid.get(oid);
        if (last && Date.now() - last < RECENT_PAID_TTL_MS)
            continue;

        const label = getOrderDisplayName(order) || oid;
        const amt = getOrderAmountYuan(order);

        try {
            const payRes = await payOrderForPlatform(token, phone, oid, pwd);
            if (payRes._paySettled) {
                recentPaid.set(oid, Date.now());
                paid++;
                lastMessage = `已支付 ${label} ¥${amt}`;
                continue;
            }
            if (isAuthTokenExpired(payRes)) {
                lastMessage = "登录已过期，已停止";
                break;
            }
            lastMessage = payRes._payError || payRes.message || `${label} 支付失败`;
        }
        catch (e) {
            lastMessage = e?.message || `${label} 支付异常`;
        }

        await sleep(350);
    }

    return {
        ok: true,
        scanned: orders.length,
        paid,
        message: lastMessage || (paid ? `本轮已支付 ${paid} 笔` : "本轮未支付成功")
    };
}
